import React from 'react';

const InputError = ({ error }) => (error ? <p className="text-red-500 text-xs mt-1">{error}</p> : null);

export const RequestCustomServiceHeader = ({ onNavigate }) => (
  <div className="bg-white border-b border-gray-200">
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
      <nav className="flex items-center space-x-2 text-sm text-gray-600 mb-6 sm:mb-8">
        <button
          onClick={() => onNavigate && onNavigate('home')}
          className="hover:text-blue-600 transition-colors"
        >
          Home
        </button>
        <span>•</span>
        <span className="text-gray-900 font-medium">Request Custom Service</span>
      </nav>

      <div className="text-center">
        <div className="text-4xl sm:text-6xl mb-4">✨</div>
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-light text-gray-900 mb-4 sm:mb-6">
          Request Custom Service
        </h1>
        <p className="text-lg sm:text-xl text-gray-600 leading-relaxed max-w-2xl mx-auto">
          Can't find what you're looking for? Tell us about your specific requirements and we'll create a customized service just for you.
        </p>
      </div>
    </div>
  </div>
);

export const RequestCustomServiceForm = ({
  onNavigate,
  formData,
  errors,
  isSubmitting,
  serviceTypes,
  timeSlots,
  durationOptions,
  participantOptions,
  budgetOptions,
  minDate,
  handleInputChange,
  handleSubmit
}) => (
  <div className="flex-1 max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="p-6 sm:p-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="bg-blue-50 rounded-lg p-4 sm:p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <span className="text-blue-600 mr-2">🎯</span>
              Service Information
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Service Category *</label>
                <select
                  name="serviceType"
                  value={formData.serviceType}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.serviceType ? 'border-red-500' : 'border-gray-300'
                  }`}
                >
                  <option value="">Select a category</option>
                  {serviceTypes.map((type) => (
                    <option key={type.id} value={type.id}>{type.name}</option>
                  ))}
                </select>
                <InputError error={errors.serviceType} />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Service Name *</label>
                <input
                  type="text"
                  name="customServiceName"
                  value={formData.customServiceName}
                  onChange={handleInputChange}
                  placeholder="e.g., Traditional Bengali Wedding, Corporate Blessing"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.customServiceName ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                <InputError error={errors.customServiceName} />
              </div>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Detailed Description *</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows={4}
                placeholder="Please describe your requirements in detail, including any specific rituals, traditions, or customs you want to include..."
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.description ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              <InputError error={errors.description} />
            </div>
          </div>

          <div className="bg-green-50 rounded-lg p-4 sm:p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <span className="text-green-600 mr-2">📅</span>
              Schedule & Location
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Preferred Date *</label>
                <input
                  type="date"
                  name="preferredDate"
                  value={formData.preferredDate}
                  onChange={handleInputChange}
                  min={minDate}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.preferredDate ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                <InputError error={errors.preferredDate} />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Preferred Time *</label>
                <select
                  name="preferredTime"
                  value={formData.preferredTime}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.preferredTime ? 'border-red-500' : 'border-gray-300'
                  }`}
                >
                  <option value="">Select time slot</option>
                  {timeSlots.map((slot) => (
                    <option key={slot} value={slot}>{slot}</option>
                  ))}
                </select>
                <InputError error={errors.preferredTime} />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Expected Duration</label>
                <select
                  name="duration"
                  value={formData.duration}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select duration</option>
                  {durationOptions.map((option) => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Number of Participants</label>
                <select
                  name="participants"
                  value={formData.participants}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select participants</option>
                  {participantOptions.map((option) => (
                    <option key={option} value={option}>{option} people</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Location *</label>
              <textarea
                name="location"
                value={formData.location}
                onChange={handleInputChange}
                rows={2}
                placeholder="Please provide the complete address where the service will be performed..."
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.location ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              <InputError error={errors.location} />
            </div>
          </div>

          <div className="bg-yellow-50 rounded-lg p-4 sm:p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <span className="text-yellow-600 mr-2">💰</span>
              Budget & Special Requirements
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Budget Range</label>
                <select
                  name="budget"
                  value={formData.budget}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select budget range</option>
                  {budgetOptions.map((option) => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>

              <div></div>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Special Requirements</label>
              <textarea
                name="specialRequirements"
                value={formData.specialRequirements}
                onChange={handleInputChange}
                rows={3}
                placeholder="Any special arrangements, dietary requirements, accessibility needs, or specific customs to follow..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="bg-purple-50 rounded-lg p-4 sm:p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <span className="text-purple-600 mr-2">📞</span>
              Contact Information
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
                <input
                  type="text"
                  name="contactName"
                  value={formData.contactName}
                  onChange={handleInputChange}
                  placeholder="Your full name"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.contactName ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                <InputError error={errors.contactName} />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number *</label>
                <input
                  type="tel"
                  name="contactPhone"
                  value={formData.contactPhone}
                  onChange={handleInputChange}
                  placeholder="10-digit phone number"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.contactPhone ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                <InputError error={errors.contactPhone} />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Email Address *</label>
                <input
                  type="email"
                  name="contactEmail"
                  value={formData.contactEmail}
                  onChange={handleInputChange}
                  placeholder="your.email@example.com"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.contactEmail ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                <InputError error={errors.contactEmail} />
              </div>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Additional Notes</label>
              <textarea
                name="additionalNotes"
                value={formData.additionalNotes}
                onChange={handleInputChange}
                rows={3}
                placeholder="Any additional information you'd like to share..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="pt-6 border-t border-gray-200">
            <div className="flex flex-col sm:flex-row gap-4 sm:justify-between sm:items-center">
              <div className="text-sm text-gray-600">
                <p>* Required fields</p>
                <p className="mt-1">Our team will contact you within 24 hours to discuss your requirements.</p>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  type="button"
                  onClick={() => onNavigate && onNavigate('home')}
                  className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`px-8 py-3 rounded-lg font-medium transition-colors ${
                    isSubmitting
                      ? 'bg-gray-400 text-white cursor-not-allowed'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  {isSubmitting ? (
                    <span className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Submitting...
                    </span>
                  ) : (
                    'Submit Request'
                  )}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  </div>
);

export const RequestCustomServiceTrustSection = () => (
  <div className="bg-white border-t border-gray-200 py-8 sm:py-12">
    <div className="max-w-7xl mx-auto px-4 sm:px-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 text-center">
        <div className="p-4">
          <div className="text-3xl sm:text-4xl mb-3 sm:mb-4">🤝</div>
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">Personal Consultation</h3>
          <p className="text-sm sm:text-base text-gray-600">Our experts will work with you to design the perfect service</p>
        </div>
        <div className="p-4">
          <div className="text-3xl sm:text-4xl mb-3 sm:mb-4">⚡</div>
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">Quick Response</h3>
          <p className="text-sm sm:text-base text-gray-600">Get a detailed quote and plan within 24 hours</p>
        </div>
        <div className="p-4">
          <div className="text-3xl sm:text-4xl mb-3 sm:mb-4">🎯</div>
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">Tailored Experience</h3>
          <p className="text-sm sm:text-base text-gray-600">Every detail customized to your specific needs and traditions</p>
        </div>
      </div>
    </div>
  </div>
);
